import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBrokerContext } from '@/app/context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import type { Customer, CreateQuotePayload } from '@/services/quotes/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import {
  isCapitalGlobalLegalPersonIdType,
  resolveCapitalGlobalIdType,
} from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import type { CapitalGlobalQuoteStepOnNextResult } from '@/features/quotes/components/capital-global/capital-global-quote-step.types';
import type {
  DiretrixDocumentLookupState,
  SubStipulantEntry,
} from '@/features/quotes/components/capital-global/types';
import {
  mapSubStipulantToCreateCustomer,
  mapSubStipulantToUpdateCustomer,
  resolveIdsAfterCreate,
} from '@/features/quotes/components/capital-global/steps/utils/map-api.util';
import {
  type SubStipulantFieldErrors,
  validateSubStipulants,
} from '@/features/quotes/components/capital-global/steps/utils/validate-risk.util';
import { defaultOneYearQuoteDates } from '@/features/quotes/components/capital-global/steps/utils/step-helpers.util';
import { useCreateCapitalGlobalQuote } from './use-create-capital-global-quote.hook';
import { useUpdateCapitalGlobalQuote } from './use-update-capital-global-quote.hook';
import { CAEPF_NOT_FOUND_MESSAGE } from '../common/use-caepf-selection.hook';

const CLIENT_INFO_STEP_CODE = 'CLIENT_INFO';
const DOCUMENT_NOT_FOUND_MESSAGE =
  'Documento não encontrado na consulta. Verifique o número informado para continuar.';
const DOCUMENT_LOOKUP_PENDING_MESSAGE =
  'Aguarde a conclusão da consulta cadastral para continuar.';

type Step1FormValues = {
  insuranceType: 'new' | 'renewal';
  congenere: string;
  policyNumber: string;
  useIndividualDocument: boolean;
  documentNumber: string;
  cpf: string;
  name: string;
  businessSegment: string;
  businessCnae: string;
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
  partnerCount?: number;
  employeeCount?: number;
  subStipulants: SubStipulantEntry[];
};

type SubmitForm = {
  state: { values: Step1FormValues };
  validateField: (...args: any[]) => Promise<unknown[]>;
  setFieldValue: (field: string, value: unknown) => void;
};

type UseSubmitCapitalGlobalStep1Params = {
  form: any;
  onNext: (result?: CapitalGlobalQuoteStepOnNextResult) => void;
  stipulantDocumentLookup: DiretrixDocumentLookupState;
  subStipulantDocumentLookups: Record<string, DiretrixDocumentLookupState>;
};

export function useSubmitCapitalGlobalStep1({
  form: formRaw,
  onNext,
  stipulantDocumentLookup,
  subStipulantDocumentLookups,
}: UseSubmitCapitalGlobalStep1Params) {
  const form = formRaw as SubmitForm;
  const queryClient = useQueryClient();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [subStipulantFieldErrors, setSubStipulantFieldErrors] = useState<
    Record<string, SubStipulantFieldErrors>
  >({});

  const { selectedBroker } = useBrokerContext();
  const {
    id,
    policyId,
    productId: contextProductId,
    productCode: contextProductCode,
  } = useQuoteParams();

  const { mutateAsync: createAsync, isPending: isCreating } =
    useCreateCapitalGlobalQuote();
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateCapitalGlobalQuote();

  const hasPolicyId = Boolean(
    (id && id.trim()) || (policyId && policyId.trim())
  );
  const resolvedPolicyId =
    (id && id.trim()) || (policyId && policyId.trim()) || '';

  async function handleSubmit() {
    setSubmitAttempted(true);

    const results = await Promise.all([
      form.validateField('documentNumber', 'change'),
      // O validador de `congenere` já se auto-desliga fora da Transferência de
      // Congênere, então não precisa de condicional aqui.
      form.validateField('congenere', 'change'),
    ]);
    if (results.some(r => Array.isArray(r) && r.length > 0)) return;

    const v = form.state.values;
    const stipulantLookupError = getDocumentLookupError(
      v.documentNumber,
      stipulantDocumentLookup
    );
    if (stipulantLookupError) {
      toaster.create({
        type: 'error',
        title: 'Documento não validado',
        description: stipulantLookupError,
      });
      return;
    }

    const nameFromLookup =
      stipulantDocumentLookup.status === 'found'
        ? stipulantDocumentLookup.resolvedName?.trim() || ''
        : '';
    const name = nameFromLookup || v.name.trim();

    if (nameFromLookup) {
      form.setFieldValue('name', nameFromLookup);
    } else {
      const nameErrors = await form.validateField('name', 'change');
      if (Array.isArray(nameErrors) && nameErrors.length > 0) return;
    }

    const segmentErrors = await form.validateField('businessSegment', 'change');
    if (Array.isArray(segmentErrors) && segmentErrors.length > 0) return;

    const cnaeFromLookup =
      !v.useIndividualDocument &&
      stipulantDocumentLookup.status === 'found'
        ? stipulantDocumentLookup.primaryActivityCode?.trim() || ''
        : '';
    const businessCnae = cnaeFromLookup || v.businessCnae.trim();

    if (cnaeFromLookup) {
      form.setFieldValue('businessCnae', cnaeFromLookup);
    } else {
      const cnaeErrors = await form.validateField('businessCnae', 'change');
      if (Array.isArray(cnaeErrors) && cnaeErrors.length > 0) return;
    }

    const subIssues = validateSubStipulants(v.subStipulants, {
      stipulantDocumentNumber: v.documentNumber,
    });
    for (const entry of v.subStipulants) {
      if (subIssues[entry.id]?.documentNumber) continue;

      const lookupError = getDocumentLookupError(
        entry.documentNumber,
        subStipulantDocumentLookups[entry.id]
      );
      if (!lookupError) continue;

      subIssues[entry.id] = {
        ...subIssues[entry.id],
        documentNumber: lookupError,
      };
      delete subIssues[entry.id].name;
    }
    if (Object.keys(subIssues).length > 0) {
      setSubStipulantFieldErrors(subIssues);
      toaster.create({
        type: 'error',
        title: 'Dados dos subestipulantes',
        description:
          'Corrija os campos destacados em cada subestipulante antes de continuar.',
      });
      return;
    }
    setSubStipulantFieldErrors({});

    const isRenewal = v.insuranceType === 'renewal';
    const idType = resolveCapitalGlobalIdType({
      documentNumber: v.documentNumber,
      useIndividualDocument: v.useIndividualDocument,
    });
    const isLegalPerson = isCapitalGlobalLegalPersonIdType(idType);
    const typeOfInsurance = isRenewal ? '3' : '1';

    const baseCustomer = {
      registerNumber: removeFormatRegisterNumber(v.documentNumber),
      // CAEPF: CPF consultado à parte (só quando houver — CPF/CNPJ não enviam).
      ...(v.cpf ? { cpf: removeFormatRegisterNumber(v.cpf) } : {}),
      idType,
      name,
      socialName: '',
      ...(Number.isFinite(v.averagePartnerAge)
        ? { averagePartnerAge: v.averagePartnerAge }
        : {}),
      ...(Number.isFinite(v.averageEmployeeAge)
        ? { averageEmployeeAge: v.averageEmployeeAge }
        : {}),
      ...(Number.isFinite(v.partnerCount) ? { partnerCount: v.partnerCount } : {}),
      ...(Number.isFinite(v.employeeCount)
        ? { employeeCount: v.employeeCount }
        : {}),
      segment: v.businessSegment || '',
      cnae: businessCnae,
    };

    const agents = selectedBroker
      ? [
          {
            name: selectedBroker.name,
            agentCode: selectedBroker.code || '',
            shareRate: 0,
            commission: 0,
            advisoryId: selectedBroker.advisoryId || '',
            registerNumber: selectedBroker.licenseNumber || '',
          },
        ]
      : undefined;

    try {
      if (hasPolicyId) {
        await handleUpdate({
          v,
          baseCustomer,
          typeOfInsurance,
          isLegalPerson,
          isRenewal,
        });
        return;
      }
      await handleCreate({
        v,
        baseCustomer,
        typeOfInsurance,
        isLegalPerson,
        isRenewal,
        agents,
      });
    } catch (error) {
      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado';
      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description,
      });
    }
  }

  async function handleUpdate({
    v,
    baseCustomer,
    typeOfInsurance,
    isLegalPerson,
    isRenewal,
  }: {
    v: Step1FormValues;
    baseCustomer: Record<string, unknown>;
    typeOfInsurance: string;
    isLegalPerson: boolean;
    isRenewal: boolean;
  }) {
    const customer: Customer = {
      ...baseCustomer,
      documentNumber: removeFormatRegisterNumber(v.documentNumber),
      documentType: String(baseCustomer.idType ?? ''),
      cellphone: '',
      phone: '',
      email: '',
      isInsured: 'Y',
      isPolicyHolder: 'Y',
      isOrgParty: 'N',
      genderCode: '',
      ...(isLegalPerson ? {} : { dateOfBirth: '' }),
    } as Customer;

    const customerList = v.subStipulants.map(entry =>
      mapSubStipulantToUpdateCustomer(entry)
    );

    const updateResult = await updateAsync({
      id: resolvedPolicyId,
      payload: {
        step: CLIENT_INFO_STEP_CODE,
        typeOfInsurance,
        previousPolicyNumber: isRenewal ? v.policyNumber || '' : '',
        ...(isRenewal ? { bonus: '0' } : {}),
        congener: (isRenewal && v.congenere) || null,
        customer,
        customerList,
      },
    });

    if (updateResult.success) {
      await queryClient.refetchQueries({
        queryKey: ['proposal-details', resolvedPolicyId],
      });
      onNext();
    }
  }

  async function handleCreate({
    v,
    baseCustomer,
    typeOfInsurance,
    isLegalPerson,
    isRenewal,
    agents,
  }: {
    v: Step1FormValues;
    baseCustomer: Record<string, unknown>;
    typeOfInsurance: string;
    isLegalPerson: boolean;
    isRenewal: boolean;
    agents: CreateQuotePayload['agents'];
  }) {
    const numericProductId = Number(contextProductId);
    if (!contextProductId || Number.isNaN(numericProductId)) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Produto não identificado. Volte e selecione o produto novamente.',
      });
      return;
    }

    const productCode = contextProductCode?.trim();
    if (!productCode) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description: 'Código do produto não encontrado.',
      });
      return;
    }

    const customer: Customer = {
      ...baseCustomer,
      ...(isLegalPerson ? {} : { gender: '', dateOfBirth: '' }),
    } as Customer;

    const customerList = v.subStipulants.map((entry, index) =>
      mapSubStipulantToCreateCustomer(entry, index)
    );

    const { start, end } = defaultOneYearQuoteDates();
    const payload: CreateQuotePayload = {
      productId: numericProductId,
      productCode,
      effectiveDate: start,
      terminateDate: end,
      quoteDate: start,
      orgCode: 'alba',
      productVersion: '1.0',
      typeOfInsurance,
      congener: (isRenewal && v.congenere) || null,
      previousPolicyNumber: isRenewal ? v.policyNumber || '' : '',
      ...(isRenewal ? { bonus: '0' } : {}),
      customer,
      customerList,
      agents,
    };

    const createResult = await createAsync(payload);
    if (createResult.success && createResult.data) {
      const { policyId: newPolicyId, quoteId } = resolveIdsAfterCreate(
        createResult.data
      );
      if (newPolicyId) onNext({ policyId: newPolicyId, quoteId });
    }
  }

  return {
    handleSubmit,
    isPending: isCreating || isUpdating,
    submitAttempted,
    subStipulantFieldErrors,
    setSubStipulantFieldErrors,
    resolvedPolicyId,
    hasPolicyId,
  };
}

function getDocumentLookupError(
  documentNumber: string,
  lookup: DiretrixDocumentLookupState | undefined
): string | undefined {
  const cleanDocument = removeFormatRegisterNumber(documentNumber);

  if (!cleanDocument) return undefined;
  if (lookup?.status === 'found' && lookup.cleanDocument === cleanDocument) {
    return undefined;
  }
  // CPF sem CAEPF vinculado: a consulta respondeu, só não há CAEPF. Bloqueia
  // (o Nome vem exclusivamente da consulta e fica travado), mas com mensagem
  // precisa em vez do genérico "documento não encontrado".
  if (lookup?.status === 'no-caepf' && lookup.cleanDocument === cleanDocument) {
    return CAEPF_NOT_FOUND_MESSAGE;
  }
  // CAEPF localizado, porém com situação cadastral irregular.
  if (
    lookup?.status === 'inactive-caepf' &&
    lookup.cleanDocument === cleanDocument
  ) {
    return lookup.helperText ?? DOCUMENT_NOT_FOUND_MESSAGE;
  }
  if (lookup?.status === 'loading' || lookup?.status === 'idle') {
    return DOCUMENT_LOOKUP_PENDING_MESSAGE;
  }
  return DOCUMENT_NOT_FOUND_MESSAGE;
}
